import Button from '@/features/shared/Button';
import Image from '@/features/shared/Image';
import {
  MuiAvatar,
  MuiCard,
  MuiCardActions,
  MuiCardContent,
  MuiCardHeader,
  MuiCardMedia,
  MuiTypography,
} from '@/features/shared/material-ui';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import clsx from 'clsx';
import React, { memo, PropsWithChildren, ReactElement, useMemo } from 'react';
import useContentCardStyles from './ContentCard.styles';

export interface ContentCardProps {
  data?: CardType;
  cardColor?: string;
  theme?: ThemeType;
  className?: string;
  elevation?: number;
  imageType?: any;
  renderCardHeading?: ReactElement;
  renderCardDescription?: ReactElement;
}

const ContentCard = ({
  data,
  cardColor,
  theme,
  className: overrideClassName,
  elevation = 8,
  imageType = 'base',
  renderCardHeading,
  renderCardDescription,
}: ContentCardProps): JSX.Element => {
  const classes = useContentCardStyles({});

  const { cardImage, cardLogo, cardDescription, cardHeading, buttonLink, cardBackgroundImage } =
    data || {};

  const hasBackgroundImage = useMemo(() => cardBackgroundImage, [cardBackgroundImage]);

  const cardClassName = clsx(
    'gntc-content-card',
    classes.card,
    `${theme}-theme`,
    `bg-color-${cardColor}`,
    overrideClassName,
    hasBackgroundImage && 'has-background-image',
  );

  const hasCardImage = useMemo(
    () => cardImage && !cardBackgroundImage,
    [cardBackgroundImage, cardImage],
  );

  const ContentWrapper = useMemo(
    () =>
      ({ children }: PropsWithChildren<any>) => {
        if (!hasBackgroundImage) return children;
        return (
          <div className={clsx(classes.cardContentWrapper, 'card-content-wrapper')}>{children}</div>
        );
      },
    [hasBackgroundImage, classes.cardContentWrapper],
  );

  return (
    <MuiCard className={cardClassName} elevation={elevation}>
      {cardBackgroundImage && (
        <div className={clsx(classes.cardBackgroundImageWrapper, 'wrapper-overlay')}>
          <Image image={cardBackgroundImage} type="background" size="cover" />
        </div>
      )}
      <ContentWrapper>
        {hasCardImage && (
          <MuiCardMedia className={classes.image}>
            <Image
              image={{ ...cardImage, fullWidthImage: true }}
              type={imageType}
              roundedCorners={false}
              base="small"
            />
          </MuiCardMedia>
        )}
        {cardLogo?.original?.url && (
          <MuiCardHeader
            className={classes.cardHeader}
            avatar={<MuiAvatar src={cardLogo?.original.url} />}
          />
        )}
        {cardDescription && (
          <MuiCardContent className={classes.content}>
            {cardHeading && !renderCardHeading && (
              <MuiTypography
                className={classes.contentHeading}
                variant="h6"
                dangerouslySetInnerHTML={{ __html: cardHeading }}
              />
            )}
            {renderCardHeading && renderCardHeading}
            {renderCardDescription && renderCardDescription}
            {!renderCardDescription && (
              <CaptureClicksRichText
                theme={theme}
                className={classes.copy}
                content={cardDescription}
              />
            )}
          </MuiCardContent>
        )}
        {buttonLink && (
          <MuiCardActions className={classes.actions}>
            <Button
              theme={theme}
              link={buttonLink}
              className="gtm-cta"
              data-testid="content-list--cta"
            />
          </MuiCardActions>
        )}
      </ContentWrapper>
    </MuiCard>
  );
};

export default memo(ContentCard);
